import { motion } from "framer-motion";
import { Camera, UserCheck, CircleCheckBig } from "lucide-react";

const STEP_IMAGE_1 =
  "https://images.unsplash.com/photo-1788623114988-bd8429d61bc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwaG9uZXxlbnwwfHx8fDE3OTI4NzY2NjJ8MA&ixlib=rb-4.1.0&q=85";
const STEP_IMAGE_2 =
  "https://images.unsplash.com/photo-1642873965196-bf664f721006?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwc2FmZXR5JTIwaGF6YXJkfGVufDB8fHx8MTc5Mjg3NjY2OHww&ixlib=rb-4.1.0&q=85";

const STEPS = [
  {
    icon: Camera,
    title: "Snap a hazard",
    text: "Take a photo of anything that does not look right on site, right from your phone.",
  },
  {
    icon: UserCheck,
    title: "AI describes it and a person reviews",
    text: "The app writes a plain-language description, and a competent person checks every suggestion before it goes out.",
  },
  {
    icon: CircleCheckBig,
    title: "Assign and close out in HazardLoop",
    text: "Send it to the person who can fix it, then track it through to closeout.",
  },
];

export const HowItWorks = () => {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-safety">
            How it works
          </p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            From hazard to closeout in three steps
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              data-testid={`how-it-works-step-${i + 1}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="rounded-xl border border-slate-200 bg-slate-50 p-8 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-base font-bold text-white">
                  {i + 1}
                </span>
                <step.icon className="h-7 w-7 text-safety" aria-hidden="true" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-900 sm:text-2xl">
                {step.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600">{step.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <motion.img
            src={STEP_IMAGE_1}
            alt="Worker on site snapping a hazard with a phone"
            data-testid="how-it-works-image-1"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="h-64 w-full rounded-xl object-cover shadow-sm"
            loading="lazy"
          />
          <motion.img
            src={STEP_IMAGE_2}
            alt="Industrial safety hazard signage"
            data-testid="how-it-works-image-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="h-64 w-full rounded-xl object-cover shadow-sm"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};
